import ContactUsSheet from '@/components/ContactUsSheet';
import { PSHeaderBackButton } from '@/components/PSHeaderButtons';
import SettingsGroup from '@/components/SettingsGroup';
import PageScrollView from '@/components/ui/PageScrollView';
import { endSession } from '@/src/lib/sessionStore';
import { showAlert } from '@/src/utils/alert';
import { Stack, useRouter } from 'expo-router';
import Head from 'expo-router/head';
import { useState } from 'react';
import { getTokenValue, Text, YStack } from 'tamagui';

/**
 * Settings — the account/support/about hub reached from the profile screen's
 * header cog. Same frame as every top-level screen: a transparent native header
 * with only a back control (PageScrollView starts content below it), and the
 * page title rendered as the first content heading rather than in the native
 * header bar — matching Profile's "Your Details" / "Tickets" convention (the
 * native title would render in the OS default font, not our $body face).
 *
 * The body is four SettingsGroups down the page: three titled sections (Account,
 * Support, About us) and a final UNTITLED group holding the two account-lifecycle
 * actions. Logout and Delete account are the group's `destructive` rows (red) and
 * both confirm first — they're irreversible, so a stray tap shouldn't fire them.
 * Logout takes a showAlert; delete is a bigger claim than an alert can carry and
 * opens the `/delete-account` modal flow, which shows what the user is about to
 * lose before it will take the word for it.
 */
export default function Settings() {
  const router = useRouter();
  const [isContactOpen, setIsContactOpen] = useState(false);

  const confirmLogout = () =>
    showAlert('Log out?', 'You can sign back in anytime.', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Log out', style: 'destructive', onPress: endSession },
    ]);

  return (
    <>
      <Head>
        <title>Settings · Putting Scene</title>
      </Head>
      <Stack.Screen
        options={{
          headerTransparent: false,
          headerTitle: () => (
            <Text
              fontSize={'$large'}
              fontWeight={'$semibold'}
              textAlign="center"
            >
              Settings
            </Text>
          ),
          headerStyle: {
            backgroundColor: getTokenValue('$background'),
          },

          // headerBlurEffect: 'extraLight',
          // headerLargeTitleEnabled: true,
          headerLeft: () => <PSHeaderBackButton />,

          // we have to put this opaque text otherwise the title "settings" isn't centered on android
          headerRight: () => <Text opacity={0}>hmm</Text>,

          unstable_headerLeftItems: () => [
            {
              type: 'custom',
              element: <PSHeaderBackButton />,
              hidesSharedBackground: true,
            },
          ],
          // we have to put this opaque text otherwise the title "settings" isn't centered on android
          unstable_headerRightItems: () => [
            {
              type: 'custom',
              element: <Text opacity={0}>hmm</Text>,
              hidesSharedBackground: true,
            },
          ],
        }}
      />
      <PageScrollView headerTransparent={false}>
        <YStack gap="$space.xl">
          {/* Deliberately not narrowed to an entitled membership: a purchase
              still confirming is a row on that screen too, and cancelling it is
              the one thing a member might come here to do. */}
          <SettingsGroup
            title="Account"
            items={[
              {
                icon: 'person-outline',
                label: 'Edit profile',
                onPress: () => {
                  router.push('/profile/edit');
                },
              },
              {
                icon: 'diamond-outline',
                label: 'Your memberships',
                onPress: () => {
                  router.push('/my-memberships');
                },
              },
            ]}
          />

          <SettingsGroup
            title="Support"
            items={[
              // {
              //   icon: 'help-circle-outline',
              //   label: 'Frequently asked questions',
              //   onPress: () => {},
              // },
              {
                icon: 'chatbubble-ellipses-outline',
                label: 'Contact us',
                onPress: () => setIsContactOpen(true),
              },
            ]}
          />

          <SettingsGroup
            title="About us"
            items={[
              {
                icon: 'shield-checkmark-outline',
                label: 'Privacy policy',
                onPress: () => {
                  router.push('/privacy-policy');
                },
              },
              {
                icon: 'document-text-outline',
                label: 'Terms and conditions',
                onPress: () => {
                  router.push('/terms-and-conditions');
                },
              },
            ]}
          />

          <SettingsGroup
            items={[
              {
                icon: 'log-out-outline',
                label: 'Logout',
                destructive: true,
                onPress: confirmLogout,
              },
              {
                icon: 'trash-outline',
                label: 'Delete Account',
                destructive: true,
                onPress: () => router.push('/delete-account'),
              },
            ]}
          />
        </YStack>
      </PageScrollView>
      <ContactUsSheet
        open={isContactOpen}
        onClose={() => setIsContactOpen(false)}
      />
    </>
  );
}
